import { ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import { appWithTranslation, useTranslation } from 'next-i18next';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { LinkedInInsightTag } from 'nextjs-linkedin-insight-tag';

// import { GoogleTagManager } from '@next/third-parties/google';
import CookieBannerClickTracker from '../components/common/CookieBannerClickTracker/CookieBannerClickTracker';
import Footer from '../components/common/Footer';
import GclidTracker from '../components/common/GclidTracker/GclidTracker';
import Header from '../components/common/Header';
import { generateCurrentPath } from '../components/common/Layout/utils';
import { JSON_SCHEMA_LD, SITE_URL } from '../constants';
import '../styles/antReset.scss';
import './global.scss';

function MyApp({ Component, pageProps }: AppProps<any>) {
  const { t: headerT } = useTranslation('header');
  const { locale, pathname } = useRouter();
  dayjs.locale(locale);

  // http://localhost:3000/resources/glossary -> /resources/glossary
  const currentPath = generateCurrentPath(pathname);
  const canonicalUrl = `${SITE_URL}${currentPath.endsWith('/') ? currentPath : `${currentPath}/`}`;

  const isBestExecutionPage = [
    '/product/best-execution/',
    '/fr-FR/produit/best-execution/',
  ].includes(currentPath);
  const isHomePage = ['/', '/fr-FR/'].includes(currentPath);

  const enableTransparentHeader = [
    '/',
    '/fr-FR/',
    '/product/cash-management',
    '/product/fx-management',
    '/product/co-pilot',
    '/product/trade-workflow-automation',
  ].includes(currentPath);

  const headerData = headerT('header', { returnObjects: true }) as any;
  return (
    <ConfigProvider>
      <LinkedInInsightTag />
      {/* <GoogleTagManager gtmId="GTM-5LX9WMP" /> */}
      <Script
        id="gtm"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-5LX9WMP');
          `,
        }}
      />

      <CookieBannerClickTracker />
      <GclidTracker />
      <Head>
        {currentPath && <link rel="canonical" href={canonicalUrl} />}
        {currentPath && <link rel="alternate" hrefLang="x-default" href={SITE_URL + currentPath} />}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(JSON_SCHEMA_LD),
          }}
        />
      </Head>
      <Header
        data={headerData}
        currentPath={currentPath}
        enableTransparentHeader={enableTransparentHeader}
      />
      <Component {...pageProps} />
      <Footer isHomePage={isHomePage} isBestExecutionPage={isBestExecutionPage} />
    </ConfigProvider>
  );
}

export default appWithTranslation(MyApp);
